import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { SharedTextoRecortado } from "shared/table";
import { BotonEliminarProducto } from "../components/BotonEliminarProducto";
import {
  COLOR_TOTAL_PEDIDO,
  COLUMNAS_PRODUCTO_SELECCIONADO as COL,
  ENCABEZADOS_PRODUCTO_SELECCIONADO as ENC,
  ETIQUETA_TOTAL_PEDIDO,
} from "../constants/productoSeleccionado.constants";
import type {
  FilaProductoSeleccionado,
  OpcionesColumnasInternas,
} from "../types/productoSeleccionado.types";
import { faltaComentario, formatearMoneda } from "../utils/productoSeleccionado.utils";
import { CeldaCantidadEditable } from "../components/CeldaCantidadEditable";
import { CeldaObservacionesEditable } from "../components/CeldaObservacionesEditable";
import { CeldaProducto } from "../components/CeldaProducto";
import { IconButton, Tooltip } from "@mui/material";
import { IconMessage, IconMessageOff, IconMessagePlus } from "@tabler/icons-react";

type Columna = ColumnDef<FilaProductoSeleccionado, unknown>;

/**
 * Columnas de la tabla de productos seleccionados.
 * Todas revisan `esTotal` porque la fila del pie usa las mismas columnas.
 */
export const useColumnasProductoSeleccionado = ({
  handleRemoveDetalle,
  onCambiarCantidadLinea,
  onCambiarObservacionesLinea,
  comentarioAbierto,
  onAlternarComentario,
  deshabilitado = false,
  permitirOrdenar = true,
}: OpcionesColumnasInternas): Columna[] =>
  useMemo<Columna[]>(() => {
    const columnas: Columna[] = [
      {
        id: COL.numero,
        header: ENC.numero,
        enableSorting: false,
        meta: { ancho: 48, alineacion: "center", ocultarEnMovil: true },
        cell: ({ row }) => (row.original.esTotal ? null : row.index + 1),
      },
      {
        accessorKey: COL.producto,
        header: ENC.producto,
        meta: { ancho: 300 },
        cell: ({ row }) =>
          row.original.esTotal ? (
            ETIQUETA_TOTAL_PEDIDO
          ) : (
            <CeldaProducto
              nombre={row.original.nombre}
              clave={row.original.clave}
              tipoProducto={row.original.tipo_producto}
              comentarios={row.original.observaciones}
              ocultarComentario={comentarioAbierto?.(row.original)}
            />
          ),
      },
      {
        accessorKey: COL.cantidad,
        header: ENC.cantidad,
        meta: { ancho: 100, alineacion: "center" },
        cell: ({ row }) => {
          if (row.original.esTotal) return null;
          if (!onCambiarCantidadLinea) return row.original.cantidad;
          return (
            <CeldaCantidadEditable
              cantidad={row.original.cantidad}
              deshabilitado={deshabilitado}
              onCambiar={(n) => onCambiarCantidadLinea(row.original.id_linea, n)}
            />
          );
        },
      },
      {
        accessorKey: COL.unidad_medida,
        header: ENC.unidad_medida,
        meta: { ancho: 100, alineacion: "center" },
        cell: ({ row }) => (row.original.esTotal ? null : row.original.unidad_medida),
      },
      {
        accessorKey: COL.precioUnitario,
        header: ENC.precioUnitario,
        meta: { ancho: 100, alineacion: "right", sinCorte: true },
        cell: ({ row }) =>
          row.original.esTotal ? null : formatearMoneda(row.original.precio_unitario),
      },
      {
        accessorKey: COL.total,
        header: ENC.total,
        meta: { ancho: 100, alineacion: "right", sinCorte: true },
        cell: ({ row }) => (
          <span
            style={{
              fontWeight: 700,
              color: row.original.esTotal ? COLOR_TOTAL_PEDIDO : undefined,
              fontSize: row.original.esTotal ? "1rem" : undefined,
            }}
          >
            {formatearMoneda(row.original.precio_total)}
          </span>
        ),
      },
    ];

    if (onCambiarObservacionesLinea && onAlternarComentario) {
      columnas.push({
        id: COL.comentario,
        header: ENC.comentario,
        enableSorting: false,
        meta: { ancho: 48, alineacion: "center" },
        cell: ({ row }) => {
          if (row.original.esTotal) return null;
          const abierto = comentarioAbierto?.(row.original) ?? false;
          const falta = faltaComentario(row.original);
          return (
            <Tooltip title={falta ? "Comentario obligatorio" : abierto ? "Ocultar comentario" : "Comentario"}>
              <span>
                <IconButton
                  size="small"
                  disabled={deshabilitado || falta}
                  onClick={() => onAlternarComentario(row.original.id_linea)}
                  aria-label={`Comentario de ${row.original.nombre}`}
                >
                  {abierto ? (
                    <IconMessageOff size={18} stroke={2} color={falta ? undefined : "#ff3a3a"} />
                  ) : row.original.observaciones ? (
                    <IconMessage size={18} stroke={2} color="#4a6fa5" />
                  ) : (
                    <IconMessagePlus size={18} stroke={2} color="#6fa8dc" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      });
    }

    if (handleRemoveDetalle) {
      columnas.push({
        id: COL.eliminar,
        header: ENC.eliminar,
        enableSorting: false,
        meta: { ancho: 56, alineacion: "center" },
        cell: ({ row }) =>
          row.original.esTotal ? null : (
            <BotonEliminarProducto
              deshabilitado={deshabilitado}
              nombreProducto={row.original.nombre}
              onEliminar={() => handleRemoveDetalle(row.original.id_linea)}
            />
          ),
      });
    }

    return permitirOrdenar
      ? columnas
      : columnas.map((columna) => ({ ...columna, enableSorting: false }));
  }, [
    handleRemoveDetalle,
    onCambiarCantidadLinea,
    onCambiarObservacionesLinea,
    comentarioAbierto,
    onAlternarComentario,
    deshabilitado,
    permitirOrdenar,
  ]);
